/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button, IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable } from 'mobx';
import * as React from 'react';
import { IDXC_GetCityStateMasterResponse, IDXC_GetCountryMasterResponse, IDXC_GetStockistsServiceResponse } from '../../data-actions/generated/DataServiceEntities.g';
import { getStockcistsCityorStateAction, GetStockistsCityOrStateInput } from '../../data-actions/stockists/stockists-cityorstate-data';
import { getStockcistsCountryRegionsAction, GetStockistsCountryRegionsInput } from '../../data-actions/stockists/stockists-country-data';
import { getStockcistsStoreAction, GetStockistsStoreInput } from '../../data-actions/stockists/stockists-store';
import { IDXC_CountryView, IDXC_StockistsTable } from '../../modal/stockistsListTable';
import { ICittaStockistsData } from './citta-stockists.data';
import { ICittaStockistsProps } from './citta-stockists.props.autogenerated';

export interface ICittaStockistsState {
    countrySelected: string;
    stateorcitySelected: string;
    searchString: string;
    countryList: [];
    stateList: [];
    stockistList: [];
}

export interface ICittaStockistsViewProps extends ICittaStockistsProps<ICittaStockistsData> {
    stockistMain: INodeProps;
    stockistGrid: INodeProps;
    StockistContainer: IModuleProps;
    inputWrap: INodeProps;
    stockistTop: INodeProps;
    inputThirds: INodeProps;
    createStockistsAddress: React.ReactNode;
    createCountryDropDown: React.ReactNode;
    createCityStateDropDown: React.ReactNode;
    createSearchAddress: React.ReactNode;
}

/**
 *
 * CittaStockists component
 * @extends {React.PureComponent<ICittaStockistsProps<ICittaStockistsData>>}
 */
class CittaStockists extends React.PureComponent<ICittaStockistsProps<ICittaStockistsData>> {
    public state: ICittaStockistsState = {
        countrySelected: '',
        stateorcitySelected: '',
        searchString: '',
        countryList: [],
        stateList: [],
        stockistList: []
    };

    @observable private _countryList: IDXC_GetCountryMasterResponse | undefined;
    @observable private _stateList: IDXC_GetCityStateMasterResponse | undefined;
    @observable private _stockistList: IDXC_GetStockistsServiceResponse | undefined;

    private _countrySelectRef: React.RefObject<HTMLSelectElement>;
    private _stateorcitySelectRef: React.RefObject<HTMLSelectElement>;
    private _searchInputRef: React.RefObject<HTMLInputElement>;

    public constructor(props: ICittaStockistsProps<ICittaStockistsData>) {
        super(props);
        this._countrySelectRef = React.createRef();
        this._stateorcitySelectRef = React.createRef();
        this._searchInputRef = React.createRef();
    }

    public async componentDidMount(): Promise<void> {
        this._getCountryList().catch(err => console.log(err));
        this._getStateProvinces('').catch(err => console.log(err));
        this._getStockistsList().catch(err => console.log(err));
    }
    public render(): JSX.Element {
        const viewProps = {
            ...this.props,
            StockistContainer: {
                moduleProps: this.props,
                className: classnames('infoSection')
            },
            stockistTop: {
                tag: 'div' as NodeTag,
                className: classnames('stockistTop', 'inputThirds')
            },
            stockistMain: {
                tag: 'div' as NodeTag,
                className: classnames('stockistMain')
            },
            stockistGrid: {
                tag: 'div' as NodeTag,
                className: classnames('stockistGrid')
            },
            createCityStateDropDown: this._renderCityStateDropDown(),
            createCountryDropDown: this._renderCountryDropDown(),
            createStockistsAddress: this._renderStockistsAddress(),
            createSearchAddress: this._renderSearchAddress(),
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }
    private _renderSearchAddress(): React.ReactNode {
        const searchPlaceholder = this.props && this.props.resources && this.props.resources.searchPlaceholder ? this.props.resources.searchPlaceholder : 'Search by Address';
        return (
            <form className='stockists-form inputWrap inputFW addressSearch' >
                <div className='searchText'>
                    <div className='c-defInput'>
                        <input
                            ref={this._searchInputRef}
                            type='text'
                            aria-label='Address Search'
                            autoComplete='off'
                            className='addressSearch'
                            placeholder={searchPlaceholder}
                            onChange={this._changeSearchText(this._searchInputRef)}
                        />
                    </div>
                </div>
                <div className='stockistsearch-submit'>
                    <Button
                        className='stockistsearch-submit-button'
                        title={'Search'}
                        color='primary'
                        onClick={this._handleSearch}
                        aria-label='Search'
                    />
                </div>
            </form>
        );
    }
    private _handleSearch = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const searchStr = (this._searchInputRef && this._searchInputRef.current && this._searchInputRef.current.value) ? this._searchInputRef.current.value : '';
        const countryValue = (this._countrySelectRef && this._countrySelectRef.current && this._countrySelectRef.current.value) ? this._countrySelectRef.current.value : '';
        const stateValue = (this._stateorcitySelectRef && this._stateorcitySelectRef.current && this._stateorcitySelectRef.current.value) ? this._stateorcitySelectRef.current.value : '';
        try {
            this._getStockistsList(countryValue, stateValue, searchStr).catch(err => console.log(err));
        } catch (error) {
            console.log(error);
        }
    }
    private _changeSearchText = (ref: React.RefObject<HTMLInputElement>) => (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState(
            { searchString: e.target.value }
        );
    }
    private _renderCityStateDropDown(): React.ReactNode {
        const citaAndStatePlaceholder = this.props && this.props.resources && this.props.resources.citaAndStatePlaceholder ? this.props.resources.citaAndStatePlaceholder : 'All Cities/States';
        return (
            <div className='inputWrap'>
                <div className='c-defSelWrap'>
                    <select
                        ref={this._stateorcitySelectRef}
                        className='country-picker'
                        aria-label='State or City'
                        name='stateorcity'
                        id='stateorcityselect'
                        value={this.state.stateorcitySelected}
                        onChange={this._onStateorCityChange}
                    >
                        <option value='' selected={true} aria-selected={true}>{citaAndStatePlaceholder}</option>
                        {this._renderStateorCityList()}
                    </select>
                </div>
            </div>
        );
    }
    private _renderCountryDropDown(): React.ReactNode {
        const countryPlaceholder = this.props && this.props.resources && this.props.resources.countryPlaceholder ? this.props.resources.countryPlaceholder : 'All Country';
        return (
            <div className='inputWrap'>
                <div className='c-defSelWrap'>
                    <select
                        ref={this._countrySelectRef}
                        className='country-picker'
                        aria-label='Countries'
                        name='countries'
                        id='countryselect'
                        value={this.state.countrySelected}
                        onChange={this._onCountryChange}
                    >
                        <option value='' selected={true} aria-selected={true}>{countryPlaceholder}</option>
                        {this._renderCountryList()}
                    </select>
                </div>
            </div>
        );
    }
    private _onStateorCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ stateorcitySelected: e.target.value || '' });
        this._getStockistsList(this.state.countrySelected, e.target.value).catch(err => console.log(err));
    }
    private _renderStateorCityList(): JSX.Element[] | null {
        const stateorcity = this.state.stateList as IDXC_GetCityStateMasterResponse;
        if (stateorcity && stateorcity.CityStateMaster) {
            return stateorcity.CityStateMaster.map((stateorcityItem) =>
                this._getCities(stateorcityItem)
            );
        } else {
            return null;
        }
    }
    private _getCities(stateorcityItem: string): JSX.Element {
        return <option value={stateorcityItem} selected={false} aria-selected={false}>{stateorcityItem}</option>;
    }
    private _renderCountryList(): JSX.Element[] | null {
        const country = this.state.countryList as IDXC_GetCountryMasterResponse;
        if (country && country.CountryMaster) {
            return country.CountryMaster.map((countryItem: IDXC_CountryView) =>
                this._getCountries(countryItem)
            );
        } else {
            return null;
        }
    }
    private _getCountries(countryItem: IDXC_CountryView): JSX.Element {
        return <option value={countryItem.CountryCode} selected={false} aria-selected={false}>{countryItem.CountryName}</option>;
    }
    private _onCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ countrySelected: e.target.value || '' });
        this._getStateProvinces(e.target.value).catch(err => console.log(err));
        this._getStockistsList(e.target.value).catch(err => console.log(err));
    }
    private _renderStockistsAddress(): React.ReactNode {
        const stockistList = this.state.stockistList as IDXC_GetStockistsServiceResponse;
        if (stockistList && stockistList.StockistsList) {
            return stockistList.StockistsList.map((stockist: IDXC_StockistsTable) =>
                (
                    <div className='stockistGridItem'>
                        <h3 className='c-heading3'>{stockist.Name}</h3>
                        <div className='address'>
                            <strong>{stockist.StreetNumber} {stockist.Street}</strong>
                            <div>
                                {stockist.District ? <div>{`${stockist.District}`}</div> : ''}
                                {stockist.City} {stockist.PostalCode} <br />
                                {stockist.CountryCode === 'AUS' ? stockist.State : ''} {stockist.CountryName} <br />
                                {stockist.PhoneNumber ? <a href={`tel:+1 ${stockist.PhoneNumber}`} aria-label='PhoneNumber'>{stockist.PhoneNumber}</a> : null} <br />
                                {stockist.Email ? <a href={`mailto:"${stockist.Email}"`} aria-label='Email'>{stockist.Email}</a> : null}
                            </div>
                        </div>
                    </div>
                )
            );
        } else {
            return null;
        }
    }

    private async _getCountryList(): Promise<void> {
        let response: IDXC_GetCountryMasterResponse;
        if (this.props.context && this.props.context.actionContext) {
            try {
                const input = new GetStockistsCountryRegionsInput();
                response = await getStockcistsCountryRegionsAction(input, this.props.context.actionContext);
                this._countryList = response;
                if (this._countryList) {
                    this.setState({
                        countryList: this._countryList
                    });
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    private async _getStateProvinces(countryCode: string): Promise<void> {
        let response: IDXC_GetCountryMasterResponse;
        if (this.props.context && this.props.context.actionContext) {
            try {
                const input = new GetStockistsCityOrStateInput(countryCode);
                response = await getStockcistsCityorStateAction(input, this.props.context.actionContext);
                this._stateList = response;
                if (this._stateList) {
                    this.setState({
                        stateList: this._stateList
                    });
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    private async _getStockistsList(countryCode?: string, cityorstate?: string, searchStr?: string): Promise<void> {
        let response: IDXC_GetStockistsServiceResponse;
        if (this.props.context && this.props.context.actionContext) {
            try {
                const input = new GetStockistsStoreInput(countryCode, cityorstate, searchStr);
                response = await getStockcistsStoreAction(input, this.props.context.actionContext);
                this._stockistList = response;
                if (this._stockistList) {
                    this.setState({
                        stockistList: this._stockistList
                    });
                }
            } catch (error) {
                console.log(error);
            }
        }
    }
}
export default CittaStockists;
